/**
 * Player identity, phone-side.
 *
 * A player is three arcade initials (A to Z, like a cabinet high-score
 * table) and nothing else. The initials are the only name that ever
 * leaves the phone, in the `phone-context` frame. No name, key, picture
 * or profile is sent over a pairing.
 *
 * Two ways in, both ending at the initials screen:
 *
 *   - guest  — nothing to remember beyond the initials.
 *   - signet — the user signed in with their own Nostr account through
 *              signet-login. That sign-in is a convenience on this phone
 *              only: we keep that it happened, never the name or key.
 *
 * Records written by older versions carried a free-text name, a picture
 * and a guest keypair. Those fields are ignored and never sent; the
 * player is asked for initials once.
 */

import { INITIALS_RE, isInitials } from '@forgesworn/joystick-protocol';

const ACCOUNT_KEY = 'joystick:identity';
/** localStorage key holding the player's three initials. */
export const INITIALS_KEY = 'initials';

export type AccountSource = 'guest' | 'signet';

/** How a signed-in account authenticated (shown in the account sheet). */
export type SignerMethod = 'nip07' | 'redirect' | 'bunker' | 'nsec' | 'amber';

/** How this phone signs in, whichever door the player took. */
export interface Account {
  readonly source: AccountSource;
  /** Present for signet accounts only. */
  readonly method?: SignerMethod;
}

export { INITIALS_RE, isInitials };

const A = 'A'.charCodeAt(0);

/** The starting position of a fresh set of wheels. */
export const DEFAULT_INITIALS = 'AAA';

/** Three random letters, for callers that want a non-default start. */
export function randomInitials(): string {
  const bytes = new Uint8Array(3);
  try {
    crypto.getRandomValues(bytes);
  } catch {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => String.fromCharCode(A + (b % 26))).join('');
}

/** The saved initials, or null when this phone has none (yet). */
export function loadInitials(): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(INITIALS_KEY);
    return isInitials(raw) ? raw : null;
  } catch {
    return null;
  }
}

/** Persist initials. Anything but three capital letters is refused. */
export function saveInitials(initials: string): boolean {
  if (!isInitials(initials)) return false;
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(INITIALS_KEY, initials);
  } catch {
    /* private mode / quota: the initials just won't survive a reload */
  }
  return true;
}

function persistAccount(account: Account): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(ACCOUNT_KEY, JSON.stringify(account));
  } catch {
    /* private mode / quota */
  }
}

/** Read the persisted account, or null if this phone has never been
 *  through first-run (or storage is unavailable / corrupt). Only the
 *  door is read back; any old name, picture or key is ignored. */
export function loadAccount(): Account | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(ACCOUNT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { source?: unknown; method?: unknown };
    let account: Account | null = null;
    if (parsed.source === 'guest') account = { source: 'guest' };
    else if (parsed.source === 'signet') account = signetAccount(parsed.method);
    // A record from an older version carried a name, picture or secret
    // key. Rewrite it without them so nothing stale lingers on the phone.
    if (account && Object.keys(parsed).some((key) => key !== 'source' && key !== 'method')) persistAccount(account);
    return account;
  } catch {
    return null;
  }
}

const METHODS = ['nip07', 'redirect', 'bunker', 'nsec', 'amber'] as const;

function signetAccount(method: unknown): Account {
  const known = METHODS.find((m) => m === method);
  return known ? { source: 'signet', method: known } : { source: 'signet' };
}

/** Remember that this phone is a guest. */
export function createGuestAccount(): Account {
  const account: Account = { source: 'guest' };
  persistAccount(account);
  return account;
}

/** Remember that this phone signed in through signet-login. Only the
 *  method is kept; the name, key and picture are discarded. */
export function adoptSignetAccount(facts: { method: string }): Account {
  const account = signetAccount(facts.method);
  persistAccount(account);
  return account;
}

/** Forget the account and initials on this device: the "switch
 *  account" path. */
export function clearAccount(): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(ACCOUNT_KEY);
      localStorage.removeItem(INITIALS_KEY);
    }
  } catch {
    /* ignore */
  }
}
